import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { Recipe } from '../../../types/recipe';
import type { InventoryItem } from '../../../types/inventory';
import { RoastProfileSection } from '../../roasts/component/RoastProfile';
import { RoastResultSection } from '../../roasts/component/RoastResultSection';
import { StopwatchSection } from '../../roasts/component/StopWatch';
import type { RoastProfileRow, RoastStageState } from '../../roasts/component/types';
import type { RoastStage } from '../../roasts/component/StopWatch';
import type { WeightUnit } from '../../../hooks/useWeightUnit';
import { defaultStages, type LotRow } from '../hooks/useWorkOrderForm';
import type { ArtisanProfileListItemDTO } from '../../../services/artisanProfileService';
import { resolveArtisanProfileControl } from '../artisanProfileControl';
import { Link, X } from 'lucide-react';
import { AutocompleteSelect } from '../../../components/common/AutocompleteSelect';
import { useLanguage } from '../../../features/language/useLanguage';

const NOOP = () => {};

export const RoastProfileSectionWrapper = ({
  isEditMode,
  useRoastProfile,
  setUseRoastProfile,
  useSnapshot,
  setUseSnapshot,
  useArtisanProfile,
  setUseArtisanProfile,
  useStopwatchMode,
  setUseStopwatchMode,
  selectedBeanId,
  selectedRecipeId,
  setSelectedRecipeId,
  artisanProfiles,
  selectedArtisanProfileId,
  linkedArtisanProfileId,
  linkedArtisanProfileTitle,
  handleArtisanProfileSelect,
  onRequestUnlinkArtisanProfile,
  disableUnlinkArtisanProfile,
  recipes,
  greenBeans,
  roastData,
  handleProfileChange,
  handleStageChange,
  handleWeightInputChange,
  handleWeightOutputChange,
  handleAgtronChange,
  roastType,
  handleRoastTypeChange,
  saveAsNewRecipe,
  setSaveAsNewRecipe,
  stage,
  seconds,
  handleStopwatchMainClick,
  handleStopwatchStart,
  handleStopwatchPause,
  handleStopwatchRestart,
  stopwatchRef,
  unit,
  setUnit,
  lots,
  isCompleted,
  isSavedAsRecipe,
  allowSaveAsNewRecipeWhenCompleted,
  lockRoastType = false,
  allowRoastTypeEdit = false,
}: {
  isEditMode: boolean;
  useRoastProfile: boolean;
  setUseRoastProfile: (value: boolean) => void;
  useSnapshot: boolean;
  setUseSnapshot: (value: boolean) => void;
  useArtisanProfile: boolean;
  setUseArtisanProfile: (value: boolean) => void;
  useStopwatchMode: boolean;
  setUseStopwatchMode: (value: boolean) => void;
  selectedBeanId: string;
  selectedRecipeId: string;
  setSelectedRecipeId: (value: string) => void;
  artisanProfiles?: ArtisanProfileListItemDTO[];
  selectedArtisanProfileId?: string;
  linkedArtisanProfileId?: string;
  linkedArtisanProfileTitle?: string;
  handleArtisanProfileSelect?: (profileId: string) => void;
  onRequestUnlinkArtisanProfile?: () => void;
  disableUnlinkArtisanProfile?: boolean;
  recipes: Recipe[];
  greenBeans: InventoryItem[];
  roastData: {
    stageStates: RoastStageState[];
    profile: RoastProfileRow[];
    weightInput: number | undefined;
    weightOutput: number | undefined;
    agtron: { wholeBean: string; groundBean: string };
  };
  handleProfileChange: (profile: RoastProfileRow[]) => void;
  handleStageChange: (idx: number, field: keyof RoastStageState, val: string | number) => void;
  handleWeightInputChange: (val: number | undefined) => void;
  handleWeightOutputChange: (val: number | undefined) => void;
  handleAgtronChange: (field: 'whole' | 'ground', val: string) => void;
  roastType: string;
  handleRoastTypeChange: (val: string) => void;
  saveAsNewRecipe: boolean;
  setSaveAsNewRecipe: (value: boolean) => void;
  stage: RoastStage;
  seconds: number;
  handleStopwatchMainClick: (atSeconds: number, beanTemp: number) => void;
  handleStopwatchStart: () => void;
  handleStopwatchPause: () => void;
  handleStopwatchRestart: () => void;
  stopwatchRef: React.RefObject<HTMLDivElement | null>;
  unit: WeightUnit;
  setUnit: (value: WeightUnit) => void;
  lots: LotRow[];
  isCompleted: boolean;
  isSavedAsRecipe: boolean;
  allowSaveAsNewRecipeWhenCompleted?: boolean;
  lockRoastType?: boolean;
  allowRoastTypeEdit?: boolean;
}) => {
  const { t } = useLanguage();
  const carouselRef = useRef<HTMLDivElement>(null);
  const [activeSlide, setActiveSlide] = useState(0);
  const showMobileCarousel = useSnapshot && !!selectedRecipeId;
  // `artisanProfiles` holds the unsynced profiles the picker draws from, so it decides
  // the picker only. A linked work order offers Unlink regardless of what is left to
  // pick — otherwise syncing the company's last free profile hides the button (WO-029).
  const artisanProfileControl = resolveArtisanProfileControl({
    linkedArtisanProfileId,
    availableArtisanProfileCount: artisanProfiles?.length ?? 0,
  });

  const stagesForChildren = useMemo(
    () => roastData.stageStates ?? [],
    [roastData.stageStates]
  );
  const calculatedLotWeight = useMemo(
    () => lots.reduce((sum, l) => sum + Number(l.usedWeight || 0), 0),
    [lots]
  );

  const [prevShowCarousel, setPrevShowCarousel] = useState(showMobileCarousel);
  if (showMobileCarousel !== prevShowCarousel) {
    setPrevShowCarousel(showMobileCarousel);
    if (!showMobileCarousel) setActiveSlide(0);
  }

  useEffect(() => {
    if (!showMobileCarousel) return;
    const el = carouselRef.current;
    if (!el) return;
    const handleScroll = () => {
      const slideWidth = el.clientWidth;
      if (slideWidth === 0) return;
      setActiveSlide(Math.round(el.scrollLeft / slideWidth));
    };
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, [showMobileCarousel]);

  const scrollToSlide = (idx: number) => {
    const el = carouselRef.current;
    if (!el) return;
    el.scrollTo({ left: el.clientWidth * idx, behavior: 'smooth' });
  };

  if (!isEditMode) return null;
  const isArtisanProfileSelected = !!selectedArtisanProfileId;
  const filteredRecipes = selectedBeanId ? recipes.filter(r => r.itemId === selectedBeanId) : recipes;
  const getRecipeBeanName = (recipe: Recipe) => {
    const rawName = recipe.itemName?.trim();
    if (rawName && rawName.toLowerCase() !== 'null') return rawName;
    return greenBeans.find(bean => bean.id === recipe.itemId)?.name || t('settingsMcp.values.none');
  };

  return (
    <div className="pb-6">
      <div className="flex flex-col mb-4 ">
        <h3 className="text-lg font-medium leading-6 text-gray-900">{t('workOrders.roastProfile.title')}</h3>
        <div className="grid grid-cols-2 md:flex md:flex-row md:items-center gap-3 md:gap-4 mt-2">
          <div className="flex items-center">
            <input
              id="use-profile"
              type="checkbox"
              data-test-id="use-roast-profile-checkbox"
              checked={useRoastProfile}
              onChange={e => setUseRoastProfile(e.target.checked)}
              disabled={isCompleted}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded disabled:opacity-50"
            />
            <label htmlFor="use-profile" className="ml-1 block text-sm text-gray-900">
              {t('workOrders.roastProfile.useRoastProfile')}
            </label>
          </div>
          {useRoastProfile && (
            <>
              <div className="flex items-center">
                <input
                  id="use-stopwatch"
                  type="checkbox"
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded disabled:opacity-50"
                  checked={useStopwatchMode}
                  onChange={e => setUseStopwatchMode(e.target.checked)}
                  disabled={isCompleted}
                />
                <label htmlFor="use-stopwatch" className={`ml-2 text-sm ${isCompleted ? 'text-gray-400' : 'text-gray-700'}`}>
                  {t('workOrders.roastProfile.useStopwatchMode')}
                </label>
              </div>

              <div className="flex items-center">
                <input
                  id="use-snapshot"
                  type="checkbox"
                  data-test-id="use-snapshot-checkbox"
                  checked={useSnapshot}
                  onChange={e => setUseSnapshot(e.target.checked)}
                  disabled={isCompleted}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded disabled:opacity-50"
                />
                <label htmlFor="use-snapshot" className={`ml-2 text-sm ${isCompleted ? 'text-gray-400' : 'text-gray-900'}`}>
                  {t('workOrders.roastProfile.roastingReference')}
                </label>
              </div>

              {artisanProfileControl === 'unlink' && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-900">
                    {t('workOrders.roastProfile.artisanProfile')} - <span className="font-medium">{linkedArtisanProfileTitle || t('settingsMcp.values.none')}</span>
                  </span>
                  <button
                    type="button"
                    onClick={onRequestUnlinkArtisanProfile}
                    disabled={disableUnlinkArtisanProfile}
                    className="inline-flex items-center rounded-md border border-red-200 bg-white px-2.5 py-1 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
                  >
                    {t('workOrders.form.unlinkModal.yesUnlink')}
                  </button>
                </div>
              )}

              {artisanProfileControl === 'picker' && (
                <div className="flex items-center">
                  <input
                    id="use-artisan"
                    type="checkbox"
                    data-test-id="use-artisan-profile-checkbox"
                    checked={useArtisanProfile}
                    onChange={e => setUseArtisanProfile(e.target.checked)}
                    disabled={isCompleted}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded disabled:opacity-50"
                  />
                  <label htmlFor="use-artisan" className={`ml-2 text-sm ${isCompleted ? 'text-gray-400' : 'text-gray-900'}`}>
                    {t('workOrders.roastProfile.useArtisanProfile')}
                  </label>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {useRoastProfile && (
        <div className="mb-4 flex flex-col md:flex-row justify-end gap-4">
          {useArtisanProfile && artisanProfiles && artisanProfiles.length > 0 && (
            <div className="w-full md:w-1/3">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-1 mb-1.5">
                <Link className="h-3.5 w-3.5 text-primary-600" />
                {t('workOrders.roastProfile.linkArtisanProfile')}
              </label>
              <div className="flex items-center gap-1">
                <div className="flex-1">
                  <AutocompleteSelect
                    value={selectedArtisanProfileId || ''}
                    onChange={(val: string) => handleArtisanProfileSelect?.(val)}
                    disabled={isCompleted}
                    options={artisanProfiles.map(p => {
                      const dateStr = p.roast_date ? new Date(p.roast_date).toLocaleDateString() : t('workOrders.roastProfile.noDate');
                      const titleStr = p.title || t('workOrders.roastProfile.untitled');
                      return {
                        id: p.id,
                        label: `${titleStr} - ${dateStr}`,
                        text: titleStr,
                      };
                    })}
                    placeholder={t('workOrders.roastProfile.selectUnsyncedProfile')}
                    allowCustom={false}
                    dataTestId="artisan-profile-autocomplete"
                  />
                </div>
                {selectedArtisanProfileId && !isCompleted && (
                  <button
                    type="button"
                    onClick={() => handleArtisanProfileSelect?.('')}
                    className="p-1.5 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100 flex-shrink-0"
                    title={t('workOrders.roastProfile.clearArtisanProfile')}
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          )}
          {useSnapshot && (
            <div className="w-full md:w-1/3">
              <label className="block text-sm font-medium text-gray-700 mt-1 mb-1.5">{t('workOrders.roastProfile.roastProfiles')}</label>
              <AutocompleteSelect
                value={selectedRecipeId}
                onChange={(val: string) => setSelectedRecipeId(val)}
                disabled={isCompleted || !selectedBeanId}
                options={filteredRecipes.map(r => ({
                  id: r.id,
                  label: `${r.name} (${r.targetBeanWeight}kg) | ${getRecipeBeanName(r)} - ${r.roastType?.toUpperCase() || t('workOrders.roastProfile.noType')}`,
                  text: r.name,
                }))}
                placeholder={selectedBeanId ? t('workOrders.roastProfile.selectProfile') : t('workOrders.roastProfile.selectGreenBeanFirst')}
                allowCustom={false}
                dataTestId="snapshot-recipe-autocomplete"
              />
            </div>
          )}
        </div>
      )}

      {useRoastProfile && (
        <div
          ref={carouselRef}
          className={
            showMobileCarousel
              ? 'flex flex-row overflow-x-auto snap-x snap-mandatory scroll-smooth gap-3 md:gap-6 md:grid md:grid-cols-2 md:overflow-visible md:bg-gray-50 md:p-4 md:rounded-md md:border md:border-gray-200'
              : useSnapshot
                ? 'bg-gray-50 p-4 rounded-md border border-gray-200 md:grid md:grid-cols-2 md:gap-6'
                : 'space-y-6 bg-gray-50 p-4 rounded-md border border-gray-200'
          }
        >
          <div
            className={
              showMobileCarousel
                ? 'space-y-6 w-full flex-shrink-0 snap-start snap-always bg-gray-50 p-4 rounded-md border border-gray-200 md:w-auto md:flex-none md:bg-transparent md:p-0 md:rounded-none md:border-0'
                : 'space-y-6'
            }
          >
            <div className="flex flex-row justify-between items-center">
              <h3 className="text-xl font-semibold">{t('workOrders.roastProfile.newProfile')}</h3>
            </div>

            {useSnapshot ? (
              <>
                  {useStopwatchMode && (
                    <div ref={stopwatchRef}>
                      <StopwatchSection
                        stage={stage}
                        onMainClick={handleStopwatchMainClick}
                        onStart={handleStopwatchStart}
                        onPause={handleStopwatchPause}
                        onRestart={handleStopwatchRestart}
                      />
                    </div>
                  )}

                <RoastProfileSection
                  key="profile-section-snapshot"
                  seconds={useStopwatchMode ? seconds : 0}
                  stage={useStopwatchMode ? stage : 'idle'}
                  onDataChange={handleProfileChange}
                  mode="recipe"
                  initialProfile={roastData.profile}
                  stackLayout={useSnapshot}
                  preserveHeightGroupId="work-order-roast-profile"
                  useStopwatchMode={useStopwatchMode}
                  readOnly={isCompleted}
                  stages={stagesForChildren}
                />

                <RoastResultSection
                  stages={roastData.stageStates}
                  onStageChange={handleStageChange}
                  weightInput={roastData.weightInput}
                  calculatedWeight={calculatedLotWeight}
                  onWeightInputChange={handleWeightInputChange}
                  weightOutput={roastData.weightOutput}
                  onWeightOutputChange={handleWeightOutputChange}
                  agtronWhole={roastData.agtron.wholeBean}
                  agtronGround={roastData.agtron.groundBean}
                  onAgtronChange={handleAgtronChange}
                  mode="recipe"
                  roastType={roastType}
                  onRoastTypeChange={handleRoastTypeChange}
                  compactLayout
                  disableWeightInput={isCompleted}
                  unit={unit}
                  onToggle={setUnit}
                  readOnlyStages={isCompleted || isArtisanProfileSelected}
                  readOnlyWeights={isCompleted}
                  readOnlyAgtron={isCompleted}
                  readOnlyRoastType={lockRoastType}
                  allowRoastTypeEdit={allowRoastTypeEdit}
                  showWorkOrderHints
                />
              </>
            ) : (
              <>
                {useStopwatchMode && (
                  <StopwatchSection
                    stage={stage}
                    onMainClick={handleStopwatchMainClick}
                    onStart={handleStopwatchStart}
                    onPause={handleStopwatchPause}
                    onRestart={handleStopwatchRestart}
                  />
                )}

                <RoastProfileSection
                  key="profile-section"
                  seconds={useStopwatchMode ? seconds : 0}
                  stage={useStopwatchMode ? stage : 'idle'}
                  onDataChange={handleProfileChange}
                  mode="recipe"
                  initialProfile={roastData.profile}
                  stackLayout={useSnapshot}
                  preserveHeightGroupId={useSnapshot ? 'work-order-roast-profile' : undefined}
                  useStopwatchMode={useStopwatchMode}
                  readOnly={isCompleted}
                  stages={stagesForChildren}
                />

                <RoastResultSection
                  stages={roastData.stageStates}
                  onStageChange={handleStageChange}
                  weightInput={roastData.weightInput}
                  calculatedWeight={calculatedLotWeight}
                  onWeightInputChange={handleWeightInputChange}
                  weightOutput={roastData.weightOutput}
                  onWeightOutputChange={handleWeightOutputChange}
                  agtronWhole={roastData.agtron.wholeBean}
                  agtronGround={roastData.agtron.groundBean}
                  onAgtronChange={handleAgtronChange}
                  mode="recipe"
                  roastType={roastType}
                  onRoastTypeChange={handleRoastTypeChange}
                  compactLayout={useSnapshot}
                  disableWeightInput={isCompleted}
                  unit={unit}
                  onToggle={setUnit}
                  readOnlyStages={isCompleted || isArtisanProfileSelected}
                  readOnlyWeights={isCompleted}
                  readOnlyAgtron={isCompleted}
                  readOnlyRoastType={lockRoastType}
                  allowRoastTypeEdit={allowRoastTypeEdit}
                  showWorkOrderHints
                />
              </>
            )}
            <div>
              <label className="inline-flex items-center text-sm text-gray-700">
                <input
                  type="checkbox"
                  data-test-id="save-as-new-recipe-checkbox"
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  checked={isSavedAsRecipe || saveAsNewRecipe}
                  onChange={e => setSaveAsNewRecipe(e.target.checked)}
                  disabled={isSavedAsRecipe || (isCompleted && !allowSaveAsNewRecipeWhenCompleted)}
                />
                <span
                  className={`ml-2 ${isSavedAsRecipe || (isCompleted && !allowSaveAsNewRecipeWhenCompleted) ? 'text-gray-400' : ''}`}
                >
                  {t('workOrders.roastProfile.saveAsNewProfile')}
                </span>
              </label>
            </div>
          </div>

          {useSnapshot && selectedRecipeId && (
            <div className="space-y-6 w-full flex-shrink-0 snap-start snap-always bg-gray-50 p-4 rounded-md border border-gray-200 md:w-auto md:flex-none md:bg-transparent md:p-0 md:rounded-none md:border-t-0 md:border-r-0 md:border-b-0 md:border-l md:border-gray-300 md:pl-3 md:-ml-3">
              <div className="space-y-6">
                {(() => {
                  const recipe = filteredRecipes.find(r => r.id === selectedRecipeId);
                  const mappedStages = recipe
                    ? defaultStages.map(s => {
                      const entry = recipe.stageSummary?.[s.key];
                      const rawTemp = entry?.temp;
                      const numericTemp = typeof rawTemp === 'string' ? Number(rawTemp) : rawTemp;
                      const safeTemp = numericTemp === undefined || Number.isNaN(numericTemp) ? s.temp : numericTemp;

                      return {
                        ...s,
                        time: entry?.time || s.time,
                        temp: safeTemp,
                      };
                    })
                    : defaultStages;
                  const profile = (recipe?.profile || []).map(p => ({
                    ...p,
                    ror: p.ror ?? null,
                  }));

                  return (
                    <>
                      <h3 className="text-xl font-semibold">{t('workOrders.roastProfile.snapshotProfile')}</h3>
                      {useStopwatchMode && (
                        <div className="opacity-0 pointer-events-none" aria-hidden="true">
                          <StopwatchSection
                            stage={stage}
                            onMainClick={handleStopwatchMainClick}
                            onStart={handleStopwatchStart}
                            onPause={handleStopwatchPause}
                            onRestart={handleStopwatchRestart}
                            disableFloating
                          />
                        </div>
                      )}
                      <RoastProfileSection
                        key={`${selectedRecipeId}-${mappedStages.map(s => s.time).join('-')}`}
                        seconds={0}
                        stage="idle"
                        mode="recipe"
                        initialProfile={profile}
                        readOnly
                        stackLayout
                        preserveHeightGroupId="work-order-roast-profile"
                        stages={mappedStages || []}
                      />
                      <RoastResultSection
                        stages={mappedStages}
                        onStageChange={NOOP}
                        weightInput={recipe?.targetBeanWeight || 0}
                        calculatedWeight={recipe?.targetBeanWeight || 0}
                        onWeightInputChange={NOOP}
                        weightOutput={recipe?.targetWeightOutput || 0}
                        onWeightOutputChange={NOOP}
                        agtronWhole={recipe?.agtron?.wholeBean ? String(recipe.agtron.wholeBean) : ''}
                        agtronGround={recipe?.agtron?.groundBean ? String(recipe.agtron.groundBean) : ''}
                        onAgtronChange={NOOP}
                        mode="recipe"
                        roastType={recipe?.roastType || ''}
                        onRoastTypeChange={NOOP}
                        readOnly
                        compactLayout
                        unit={unit}
                        onToggle={setUnit}
                      />
                    </>
                  );
                })()}
              </div>
            </div>
          )}
        </div>
      )}

      {useRoastProfile && showMobileCarousel && (
        <div className="mt-3 flex justify-center gap-2 md:hidden" role="tablist" aria-label={t('workOrders.roastProfile.title')}>
          {[0, 1].map(idx => (
            <button
              key={idx}
              type="button"
              role="tab"
              aria-selected={activeSlide === idx}
              aria-label={idx === 0 ? t('workOrders.roastProfile.newProfile') : t('workOrders.roastProfile.snapshotProfile')}
              onClick={() => scrollToSlide(idx)}
              className={`h-2 rounded-full transition-all ${activeSlide === idx ? 'w-6 bg-primary-600' : 'w-2 bg-gray-300'}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};
